import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Truck, Sparkles, Gift, Check, Mail, Package } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import EnquiryForm from "../components/EnquiryForm";
import { api, formatINR } from "../lib/api";
import { useCart } from "../lib/cart";
import { Button } from "../components/ui/button";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [addons, setAddons] = useState([]);
  const [selected, setSelected] = useState({});
  const [selectedToys, setSelectedToys] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [toyError, setToyError] = useState(false);
  const toysSectionRef = useRef(null);
  const { addItem } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      if (data.min_order_quantity && data.min_order_quantity > 1) setQty(data.min_order_quantity);
    }).catch(() => setProduct(null));
    api.get(`/addons`).then(({ data }) => setAddons(data.filter((a) => a.in_stock))).catch(() => {});
  }, [id]);

  const addonsTotal = useMemo(
    () =>
      addons.reduce(
        (s, a) => s + (selected[a.id] ? a.price * selected[a.id] : 0),
        0,
      ),
    [addons, selected],
  );

  const hamperTotal = product ? (product.price + addonsTotal) * qty : 0;

  const toggleAddon = (a) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[a.id]) delete next[a.id];
      else next[a.id] = 1;
      return next;
    });
  };

  const updateAddonQty = (a, q) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (q <= 0) delete next[a.id];
      else next[a.id] = q;
      return next;
    });
  };

  // Toys the hamper offers (in-stock only). If non-empty, at least one must be picked.
  const availableToys = useMemo(
    () => (product?.toys || []).filter((t) => t.in_stock !== false),
    [product],
  );
  const toyRequired = product?.product_type === "hamper" && availableToys.length > 0;

  const handleAddToCart = (thenGoToCart = false) => {
    if (toyRequired && selectedToys.length === 0) {
      setToyError(true);
      toast.error("Please choose a toy for inside the balloon.");
      // Scroll toys section into view for clarity
      try {
        toysSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (e) { /* noop */ }
      return;
    }
    const chosenAddons = addons
      .filter((a) => selected[a.id])
      .map((a) => ({ ...a, quantity: selected[a.id] }));
    addItem(product, qty, chosenAddons, selectedToys);
    if (thenGoToCart) nav("/cart");
    else {
      setSelected({});
      setSelectedToys([]);
      setToyError(false);
    }
  };

  const toggleToy = (toyId) => {
    // Only one toy allowed per hamper — clicking again deselects, clicking a different one replaces
    setSelectedToys((prev) => (prev[0] === toyId ? [] : [toyId]));
    setToyError(false);
  };

  if (!product) {
    return (
      <div className="App">
        <Navbar />
        <div className="mx-auto max-w-7xl px-6 py-24 text-center text-[#7a7571]" data-testid="product-loading">
          Loading hamper...
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10 lg:py-14" data-testid="product-page">
        <Link
          to="/shop"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#7a7571] hover:text-[#d08c8c]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div className="overflow-hidden rounded-[2rem] border border-[#e8e2da] bg-white">
            <img
              src={product.image_base64 || product.image_url}
              alt={product.name}
              className="aspect-[4/5] w-full object-cover"
              data-testid="product-image"
            />
          </div>

          <div>
            <p className="label-tiny mb-3">{product.category}</p>
            <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl" data-testid="product-name">
              {product.name}
            </h1>
            <div className="mt-4 font-serif text-3xl text-[#d08c8c]" data-testid="product-price">
              {formatINR(product.price)}
              {product.product_type === "chocolate" && (
                <span className="ml-2 text-sm font-sans text-[#7a7571]">/ piece</span>
              )}
              {addonsTotal > 0 && product.product_type !== "chocolate" && (
                <span className="ml-3 text-base font-sans text-[#7a7571]">
                  + {formatINR(addonsTotal)} gifts
                </span>
              )}
            </div>
            <p className="mt-6 text-[#7a7571] leading-relaxed" data-testid="product-description">
              {product.description || product.short_description}
            </p>

            {product.product_type === "chocolate" ? (
              // Enquiry flow for customised chocolates
              <div className="mt-8 space-y-4">
                <div className="rounded-2xl border border-[#e8e2da] bg-[#faf1ee] p-5" data-testid="chocolate-enquiry-banner">
                  <p className="label-tiny mb-2">Enquiry-based · Min 100 pcs</p>
                  <p className="text-sm text-[#2a2928]">
                    We wrap premium chocolates with your custom design, names or
                    occasion. Send an enquiry and our team will contact you within
                    24 hours with a tailored quote & artwork options.
                  </p>
                </div>
                <Button
                  data-testid="product-enquiry-btn"
                  onClick={() => setEnquiryOpen(true)}
                  className="rounded-full bg-[#d08c8c] px-10 py-6 text-sm text-white hover:bg-[#b87a7a]"
                >
                  <Mail className="mr-2 h-4 w-4" /> Send enquiry
                </Button>
              </div>
            ) : (
              <>
                {/* TOYS: choose what goes INSIDE the balloon (MANDATORY when the hamper offers toys) */}
                {availableToys.length > 0 && (
                  <section
                    ref={toysSectionRef}
                    className={`mt-8 rounded-2xl border bg-white p-5 transition-colors ${
                      toyError ? "border-red-400 bg-red-50/40" : "border-[#e8e2da]"
                    }`}
                    data-testid="toys-section"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="label-tiny flex items-center gap-2">
                        <Package className="h-3.5 w-3.5" /> Choose a toy for inside the balloon
                        <span className="ml-1 text-red-500" aria-label="required">*</span>
                      </p>
                      <span
                        data-testid="toys-required-hint"
                        className={`text-[11px] ${
                          toyError
                            ? "font-medium text-red-600"
                            : selectedToys.length > 0
                            ? "text-emerald-700"
                            : "text-[#7a7571]"
                        }`}
                      >
                        {selectedToys.length > 0
                          ? "Selected"
                          : "Required · pick one"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {availableToys.map((t) => {
                        const on = selectedToys.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => toggleToy(t.id)}
                            data-testid={`toy-${t.id}`}
                            className={`group relative overflow-hidden rounded-2xl border p-2 text-left transition-all ${
                              on ? "border-[#d08c8c] bg-[#faf1ee]" : "border-[#e8e2da] bg-white hover:border-[#d08c8c]/60"
                            }`}
                          >
                            <img
                              src={t.image_base64 || t.image_url}
                              alt={t.name}
                              className="aspect-square w-full rounded-xl object-cover"
                            />
                            <div className="mt-2 flex items-center justify-between gap-1 text-xs">
                              <span className="line-clamp-1 text-[#2a2928]">{t.name}</span>
                              {on && <Check className="h-3.5 w-3.5 text-[#d08c8c]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* ADD-ON GIFTS (Personalise the box) */}
                {addons.length > 0 && (
                  <section className="mt-6 rounded-2xl border border-[#e8e2da] bg-white p-5" data-testid="addons-section">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="label-tiny flex items-center gap-2">
                        <Gift className="h-3.5 w-3.5" /> Personalise the box · Add gifts to your hamper
                      </p>
                      {addonsTotal > 0 && (
                        <div className="text-right" data-testid="addons-total">
                          <div className="font-serif text-lg text-[#d08c8c]">+ {formatINR(addonsTotal)}</div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {addons.map((a) => {
                        const isSelected = !!selected[a.id];
                        const qtyA = selected[a.id] || 0;
                        return (
                          <div
                            key={a.id}
                            data-testid={`addon-card-${a.id}`}
                            className={`flex gap-3 rounded-xl border p-2.5 transition-all ${
                              isSelected ? "border-[#d08c8c] bg-[#faf1ee]" : "border-[#e8e2da] bg-white"
                            }`}
                          >
                            <img
                              src={a.image_base64 || a.image_url}
                              alt={a.name}
                              className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                            />
                            <div className="flex flex-1 flex-col">
                              <h3 className="font-serif text-sm leading-tight text-[#2a2928]">{a.name}</h3>
                              <div className="mt-auto flex items-center justify-between pt-1">
                                <span className="text-xs text-[#2a2928]">{formatINR(a.price)}</span>
                                {isSelected ? (
                                  <div className="flex items-center gap-1 rounded-full border border-[#d08c8c] bg-white px-1.5 py-0.5">
                                    <button data-testid={`addon-decrease-${a.id}`} onClick={() => updateAddonQty(a, qtyA - 1)} className="rounded-full p-0.5 hover:bg-[#f4ede6]">
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="min-w-[14px] text-center text-xs">{qtyA}</span>
                                    <button data-testid={`addon-increase-${a.id}`} onClick={() => updateAddonQty(a, qtyA + 1)} className="rounded-full p-0.5 hover:bg-[#f4ede6]">
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button data-testid={`addon-add-${a.id}`} onClick={() => toggleAddon(a)} className="inline-flex items-center gap-0.5 rounded-full border border-[#e8e2da] px-2 py-0.5 text-[10px] font-medium text-[#2a2928] hover:border-[#d08c8c] hover:text-[#d08c8c]">
                                    <Plus className="h-3 w-3" /> Add
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* CTA row */}
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-3 rounded-full border border-[#e8e2da] bg-white px-3 py-2">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      data-testid="product-qty-decrease"
                      className="rounded-full p-1 hover:bg-[#f4ede6]"
                      aria-label="Decrease"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-[24px] text-center font-medium" data-testid="product-qty">{qty}</span>
                    <button
                      onClick={() => setQty((q) => q + 1)}
                      data-testid="product-qty-increase"
                      className="rounded-full p-1 hover:bg-[#f4ede6]"
                      aria-label="Increase"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <Button
                    data-testid="product-add-to-cart"
                    onClick={() => handleAddToCart(false)}
                    className="rounded-full bg-[#d08c8c] px-8 py-6 text-sm text-white hover:bg-[#b87a7a]"
                  >
                    <ShoppingBag className="mr-2 h-4 w-4" /> Add to cart
                    <span className="ml-2 font-serif text-base">· {formatINR(hamperTotal)}</span>
                  </Button>

                  <Button
                    data-testid="product-buy-now"
                    variant="outline"
                    onClick={() => handleAddToCart(true)}
                    className="rounded-full border-[#e8e2da] px-8 py-6 text-sm hover:border-[#d08c8c]"
                  >
                    Buy now
                  </Button>
                </div>
              </>
            )}

            <div className="mt-10 space-y-4 border-t border-[#e8e2da] pt-8">
              <div className="flex items-start gap-3">
                <Truck className="mt-0.5 h-5 w-5 text-[#d08c8c]" />
                <div>
                  <div className="text-sm font-semibold">Scheduled delivery</div>
                  <div className="text-sm text-[#7a7571]">
                    Pick your delivery date at checkout. We deliver fresh, on time.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="mt-0.5 h-5 w-5 text-[#d08c8c]" />
                <div>
                  <div className="text-sm font-semibold">Personalised note</div>
                  <div className="text-sm text-[#7a7571]">
                    Add a custom message on the card during checkout.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section reserved for future use */}
      </div>
      <EnquiryForm open={enquiryOpen} onOpenChange={setEnquiryOpen} product={product} />
      <Footer />
    </div>
  );
}
